import { BASE_URL } from '@/config/api';
import { toast } from 'sonner';

export interface PendingBilling {
  studentId: string;
  studentName: string;
  eventCount: number;
  totalAmount: number;
  lastEventDate: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  allDay?: boolean;
  extendedProps: {
    description?: string;
    studentIds: string[];
    categoryId: string;
    locationId?: string;
    completed?: boolean;
    recurring?: boolean;
    duration?: number; // Duration in minutes
    eventType?: 'lesson' | 'special_event' | 'student_absence' | 'coach_absence';
    billed?: boolean; // Flag to indicate if the event has been billed;
  };
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
}

// In-memory cache (legacy range cache + per-month cache for chunked fetches)
const eventsCache = new Map<string, { timestamp: number, events: CalendarEvent[] }>();
const monthCache = new Map<string, { timestamp: number, events: CalendarEvent[], promise?: Promise<CalendarEvent[]> }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const clearEventsCache = (): void => {
  eventsCache.clear();
  monthCache.clear();
};

// Pad a date to YYYY-MM-DD
const fmt = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Build the list of month chunks (1st -> last day) covering [start, end)
const getMonthChunks = (start: Date, end: Date): Array<{ key: string, start: string, end: string }> => {
  const chunks: Array<{ key: string, start: string, end: string }> = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor < end) {
    const chunkStart = new Date(cursor);
    const chunkEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    const key = `${chunkStart.getFullYear()}-${String(chunkStart.getMonth() + 1).padStart(2, '0')}`;
    chunks.push({ key, start: fmt(chunkStart), end: fmt(chunkEnd) });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return chunks;
};

const fetchRangeFromApi = async (token: string | undefined, startDate: string, endDate: string): Promise<CalendarEvent[]> => {
  const response = await fetch(`${BASE_URL}/api/events`, {
    method: 'POST',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ startDate, endDate }),
  });
  if (!response.ok) throw new Error('Failed to fetch events');
  const data = await response.json();
  return data.map((event: any) => ({
    ...event,
    start: event.start.replace(' ', 'T'),
    end: event.end.replace(' ', 'T'),
  }));
};

// Concurrency-limited runner
const runWithConcurrency = async <T,>(tasks: Array<() => Promise<T>>, limit = 3): Promise<T[]> => {
  const results: T[] = new Array(tasks.length);
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (true) {
      const idx = i++;
      if (idx >= tasks.length) return;
      results[idx] = await tasks[idx]();
    }
  });
  await Promise.all(workers);
  return results;
};

// Convert CalendarEvent to FullCalendar Event type
export const convertToFullCalendarEvent = (event: CalendarEvent) => {
  return {
    ...event,
    start: new Date(event.start),
    end: new Date(event.end)
  };
};

// Calculate duration between two times in minutes
export const calculateDuration = (startTime: string, endTime: string, startDate: string, endDate: string): number => {
  const startDateTime = new Date(`${startDate}T${startTime}`);
  const endDateTime = new Date(`${endDate}T${endTime}`);
  
  const difference = endDateTime.getTime() - startDateTime.getTime();
  
  return Math.round(difference / (1000 * 60));
};

// Update end time based on start time and duration
export const calculateEndTime = (startTime: string, durationMinutes: number): string => {
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  
  let totalMinutes = startHours * 60 + startMinutes + durationMinutes;
  
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMinutes = totalMinutes % 60;
  
  return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
};

// Calculate price based on category payment type, student count, and duration
export const calculatePrice = (category: any, studentCount: number, durationMinutes: number): number => {
  if (!category) return 0;
  
  const paymentType = category.s_std_pay_type; 
  const prices = category.prices || {};
  
  if (paymentType === 1) {
    const fixedPrice = prices.price_fix || 0;
    return parseFloat(fixedPrice) * studentCount * (durationMinutes / 60);
  } 
  else {
    let priceKey = `price_${Math.min(studentCount, 10)}`;

    if (studentCount > 20) {
      priceKey = 'price_su';
    }
    
    const hourlyRate = prices[priceKey] || prices.price_1 || 0;
    
    return parseFloat(hourlyRate) * studentCount * (durationMinutes / 60);
  }
};

// Fetch events with date range parameters.
// Internally chunks by month and caches each month independently so that
// large ranges (year view, list year with lazy-load) only fetch missing months.
export const getEvents = async (token?: string, startDate?: string, endDate?: string): Promise<CalendarEvent[]> => {
    if (!startDate || !endDate) {
        return [];
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) {
      return [];
    }

    // Short range (<= ~45 days): keep legacy single-request behaviour
    const rangeDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (rangeDays <= 45) {
      const cacheKey = `${startDate}_${endDate}`;
      const cached = eventsCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
        return cached.events;
      }
      try {
        const formatted = await fetchRangeFromApi(token, startDate, endDate);
        eventsCache.set(cacheKey, { timestamp: Date.now(), events: formatted });
        return formatted;
      } catch (error) {
        console.error('Error fetching events:', error);
        return mockEvents;
      }
    }

    // Long range: split into month chunks, reuse cached months, fetch missing ones
    const chunks = getMonthChunks(start, end);
    const now = Date.now();
    const tasks = chunks.map((chunk) => async () => {
      const cached = monthCache.get(chunk.key);
      if (cached && now - cached.timestamp < CACHE_DURATION) {
        return cached.events;
      }
      // Deduplicate concurrent in-flight requests for the same month
      if (cached?.promise) {
        return cached.promise;
      }
      const promise = (async () => {
        try {
          const events = await fetchRangeFromApi(token, chunk.start, chunk.end);
          monthCache.set(chunk.key, { timestamp: Date.now(), events });
          return events;
        } catch (error) {
          console.error(`Error fetching events for ${chunk.key}:`, error);
          monthCache.delete(chunk.key);
          return [] as CalendarEvent[];
        }
      })();
      monthCache.set(chunk.key, { timestamp: 0, events: cached?.events || [], promise });
      return promise;
    });

    try {
      const perChunk = await runWithConcurrency(tasks, 3);
      // Deduplicate events that may overlap month boundaries
      const seen = new Set<string>();
      const merged: CalendarEvent[] = [];
      for (const arr of perChunk) {
        for (const ev of arr) {
          if (seen.has(ev.id)) continue;
          seen.add(ev.id);
          merged.push(ev);
        }
      }
      // Filter strictly to requested range
      return merged.filter((ev) => {
        const evEnd = new Date(ev.end);
        const evStart = new Date(ev.start);
        return evEnd >= start && evStart < end;
      });
    } catch (error) {
      console.error('Error fetching chunked events:', error);
      return mockEvents;
    }
};

// Create a new lesson
export const createLesson = async (token?: string, lessonData?: any): Promise<any> => {
  try {
    const response = await fetch(`${BASE_URL}/api/create_lesson`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(lessonData),
      });
      
      if (response.ok) {
        const data = await response.json();
        clearEventsCache(); // Invalidate cache
        return data.event;
      }
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create lesson');
  } catch (error) {
    console.error('Error creating lesson:', error);
    throw error;
  }
};

export const createSchoolLesson = async (token?: string, lessonData?: any): Promise<any> => {
  try {
    const response = await fetch(`${BASE_URL}/api/create_school_lesson`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(lessonData),
      });
      
      if (response.ok) {
        const data = await response.json();
        clearEventsCache(); // Invalidate cache
        return data.event;
      }
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create school lesson');
  } catch (error) {
    console.error('Error creating school lesson:', error);
    throw error;
  }
};

// Update an event
export const updateEvent = async (token: string, eventId: string, data: any, userType: string, invoiceType: string): Promise<any> => {
  const url = userType === "S" || (userType === 'C' && invoiceType === "S")
      ? `${BASE_URL}/api/school/update_event/${eventId}`
      : `${BASE_URL}/api/update_event/${eventId}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update event');
    }
    const result = await response.json();
    clearEventsCache();
    return result;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const updateEventDateTime = async (token: string, eventId: string, data: { start: string, end: string }): Promise<any> => {
  try {
    const response = await fetch(`${BASE_URL}/api/events/updateDate/${eventId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update event date/time');
    }

    const updatedEvent = await response.json();
    clearEventsCache();
    return updatedEvent;
  } catch (error) {
    console.error('Error updating event date/time:', error);
    throw error;
  }
};

// Delete an event
export const deleteEvent = async (token: string, eventId: string, deleteAllRecurrences: boolean): Promise<void> => {
  try {
    const response = await fetch(`${BASE_URL}/api/delete_event`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ id: eventId, deleteAllRecurrences })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete event');
    }
    clearEventsCache();
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

export const markEventAsCompleted = async (token: string, eventId: string): Promise<void> => {
    try {
        const response = await fetch(`${BASE_URL}/api/validate_event`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ id: eventId })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to mark event as completed');
        }
        clearEventsCache();
    } catch (error) {
        console.error('Error marking event as completed:', error);
        throw error;
    }
};

export const markEventAsUncompleted = async (token: string, eventId: string): Promise<void> => {
    try {
        const response = await fetch(`${BASE_URL}/api/unvalidate_event`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ id: eventId })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to mark event as uncompleted');
        }
        clearEventsCache();
    } catch (error) {
        console.error('Error marking event as uncompleted:', error);
        throw error;
    }
};

export const updateStudentEventStatus = async (token: string, data: { eventId: string, studentId: string, status: string }): Promise<any> => {
    try {
        const response = await fetch(`${BASE_URL}/api/student/event/status`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            throw new Error('Failed to update student status');
        }
        const result = await response.json();
        clearEventsCache();
        return result;
    } catch (error) {
        console.error('Error updating student event status:', error);
        throw error;
    }
};

export const markEventsAsCompleted = async (token: string, eventIds: string[]): Promise<void> => {
    try {
        const response = await fetch(`${BASE_URL}/api/validate_events`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ eventIds })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to mark events as completed');
        }
        clearEventsCache();
    } catch (error) {
        console.error('Error marking events as completed:', error);
        throw error;
    }
};

export const deleteEvents = async (token: string, eventIds: string[]): Promise<void> => {
    try {
        const response = await fetch(`${BASE_URL}/api/delete_events`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ eventIds })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to delete events');
        }
        clearEventsCache();
    } catch (error) {
        console.error('Error deleting events:', error);
        throw error;
    }
};


// ----- The rest of the file remains the same, just including the new exports -----

// Mock data should be defined before being used
const mockEvents: CalendarEvent[] = [];
const mockStudents: any[] = [];
const mockCategories: any[] = [];
const mockLocations: any[] = [];
let teacherPrices: any[] = [];

// Get students
export const getStudents = async (user?: any, token?: string): Promise<any[]> => {
  try {
    const response = await fetch(`${BASE_URL}/api/students`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Accept' : 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch students');
    }

    const data = await response.json();
    return data.students;
  } catch (error) {
    console.error('Error fetching students:', error);
    return mockStudents;
  }
};

export const getAgendaStudentsList = async (user?: any, token?: string): Promise<any[]> => {
  try {
    const response = await fetch(`${BASE_URL}/api/agenda/students`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Accept' : 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch students');
    }

    const data = await response.json();
    return data.students;
  } catch (error) {
    console.error('Error fetching students:', error);
    return mockStudents;
  }
};

// Get teachers
export const getTeachers = async (user?: any, token?: string): Promise<any[]> => {
  try {
    const response = await fetch(`${BASE_URL}/api/teachers`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Accept' : 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch teachers');
    }

    const data = await response.json();
    return data.teachers;
  } catch (error) {
    console.error('Error fetching teachers:', error);
    return [];
  }
};

// Get categories
export const getCategories = async (user?: any, token?: string): Promise<any[]> => {
  try {
    const response = await fetch(`${BASE_URL}/api/categories`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Accept' : 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }

    const data = await response.json();
    teacherPrices = data.teachers || [];
    return data.categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return mockCategories;
  }
};

// Get locations
export const getLocations = async (user?: any, token?: string): Promise<any[]> => {
  try {
    const response = await fetch(`${BASE_URL}/api/locations`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Accept' : 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch locations');
    }

    const data = await response.json();
    return data.locations;
  } catch (error) {
    console.error('Error fetching locations:', error);
    return mockLocations;
  }
};

// Add a new student
export const addStudent = async (user?: any, token?: string, data?: { firstname: string, lastname: string }): Promise<any> => {
    const id = `new-student-${Date.now()}`;
    const newStudent = {
      id,
      name: `${data?.firstname} ${data?.lastname}`,
      firstname: data?.firstname,
      lastname: data?.lastname,
      email: '',
      phone: ''
    };
    mockStudents.push(newStudent);
    return newStudent;
};

// Add a new category
export const addCategory = async (user?: any, token?: string, data?: { name: string, color: string, price: number }): Promise<any> => {
    const id = `new-category-${Date.now()}`;
    const newCategory = {
      id,
      name: data?.name,
      color: data?.color,
      price: data?.price
    };
    mockCategories.push(newCategory);
    return newCategory;
};

// Add a new location
export const addLocation = async (user?: any, token?: string, data?: { name: string }): Promise<any> => {
    const id = `new-location-${Date.now()}`;
    const newLocation = { id, name: data?.name };
    mockLocations.push(newLocation);
    return newLocation;
};

export const getPendingBillings = async (token?: string): Promise<PendingBilling[]> => {
  try {
    const response = await fetch(`${BASE_URL}/api/billing`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Accept' : 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch pending billings');
    }

    const data = await response.json();
    return data.desiredArray;
  } catch (error) {
    console.error('Error fetching pending billings:', error);
    return [];
  }
};

export const getSchoolPendingBillings = async (token?: string): Promise<PendingBilling[]> => {
  try {
    const response = await fetch(`${BASE_URL}/api/school/billing`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Accept' : 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch pending billings');
    }

    const data = await response.json();
    return data.desiredArray;
  } catch (error) {
    console.error('Error fetching pending billings:', error);
    return [];
  }
};

export interface PendingValidationEvent {
  id: string;
  title?: string;
  date: string;
  start?: string;
  end?: string;
  duration: number;
  category: string;
  categoryId: string | number;
  categoryColor?: string;
  studentId: string;
  studentName: string;
  studentCount?: number;
  price: number;
  description?: string;
  is_locked?: number;
  allDay?: boolean;
}

export const getPendingValidationEvents = async (token?: string): Promise<PendingValidationEvent[]> => {
  try {
    const response = await fetch(`${BASE_URL}/api/billing/events/pending/students`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Accept' : 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch pending validation events');
    }

    const data = await response.json();
    const raw = data.events ?? data.data ?? data;
    console.log('Fetched pending validation events (raw):', raw);

    // Flatten: raw may be an array, an object keyed by studentId with arrays,
    // an object of events, or grouped {student, events} entries.
    const flatten = (input: any): any[] => {
      if (!input) return [];
      if (Array.isArray(input)) return input.flatMap(flatten);
      if (typeof input === 'object') {
        if (input.id && (input.date || input.start || input.starts_at)) return [input];
        if (Array.isArray(input.events)) {
          const student = input.student || input;
          return input.events.map((e: any) => ({ ...e, __student: student }));
        }
        return Object.values(input).flatMap(flatten);
      }
      return [];
    };

    const flat = flatten(raw);
    const normalized: PendingValidationEvent[] = flat.map((e: any) => {
      const s = e.__student || e.student || {};
      const firstname = e.studentFirstname ?? e.teacherFirstname ?? e.firstname ?? s.firstname ?? '';
      const lastname = e.studentLastname ?? e.teacherLastname ?? e.lastname ?? s.lastname ?? '';
      const composedName = `${firstname} ${lastname}`.trim();
      return {
        id: String(e.id),
        title: e.title,
        date: e.date || e.start || e.starts_at || e.start_date || '',
        start: e.start || e.starts_at,
        end: e.end || e.ends_at,
        duration: Number(e.duration) || 0,
        category: e.category || e.categoryName || 'Lesson',
        categoryId: e.categoryId ?? e.category_id ?? '',
        categoryColor: e.categoryColor || e.category_color || '#3498db',
        studentId: String(e.studentId ?? e.student_id ?? s.id ?? ''),
        studentName: e.studentName || composedName || e.student_name || 'Unknown',
        studentCount: e.studentCount,
        price: Number(e.price) || 0,
        description: e.description,
        is_locked: e.is_locked,
        allDay: Boolean(e.allDay ?? e.all_day ?? e.is_all_day ?? false),
      };
    });
    console.log('Normalized pending validation events:', normalized);
    return normalized;
  } catch (error) {
    console.error('Error fetching pending validation events:', error);
    return [];
  }
};

export const getSchoolPendingValidationEvents = async (token?: string): Promise<PendingValidationEvent[]> => {
  try {
    const response = await fetch(`${BASE_URL}/api/billing/events/school/pending/teachers`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch school pending validation events');
    }

    const data = await response.json();
    const raw = data.events ?? data.data ?? data;
    console.log('Fetched school pending validation events (raw):', raw);

    const flatten = (input: any): any[] => {
      if (!input) return [];
      if (Array.isArray(input)) return input.flatMap(flatten);
      if (typeof input === 'object') {
        if (input.id && (input.date || input.start || input.starts_at)) return [input];
        if (Array.isArray(input.events)) {
          const student = input.student || input;
          return input.events.map((e: any) => ({ ...e, __student: student }));
        }
        return Object.values(input).flatMap(flatten);
      }
      return [];
    };

    const flat = flatten(raw);
    const normalized: PendingValidationEvent[] = flat.map((e: any) => {
      const s = e.__student || e.student || {};
      const firstname = e.studentFirstname ?? e.teacherFirstname ?? e.firstname ?? s.firstname ?? '';
      const lastname = e.studentLastname ?? e.teacherLastname ?? e.lastname ?? s.lastname ?? '';
      const composedName = `${firstname} ${lastname}`.trim();
      return {
        id: String(e.id),
        title: e.title,
        date: e.date || e.start || e.starts_at || e.start_date || '',
        start: e.start || e.starts_at,
        end: e.end || e.ends_at,
        duration: Number(e.duration) || 0,
        category: e.category || e.categoryName || 'Lesson',
        categoryId: e.categoryId ?? e.category_id ?? '',
        categoryColor: e.categoryColor || e.category_color || '#3498db',
        studentId: String(e.studentId ?? e.student_id ?? s.id ?? ''),
        studentName: e.studentName || composedName || e.student_name || 'Unknown',
        studentCount: e.studentCount,
        price: Number(e.price) || 0,
        description: e.description,
        is_locked: e.is_locked,
        allDay: Boolean(e.allDay ?? e.all_day ?? e.is_all_day ?? false),
      };
    });
    console.log('Normalized school pending validation events:', normalized);
    return normalized;
  } catch (error) {
    console.error('Error fetching school pending validation events:', error);
    return [];
  }
};

export const getCachedEvents = (): CalendarEvent[] => {
  return [];
};

export const filterEventsByDateRange = (events: CalendarEvent[], startDate: string, endDate: string): CalendarEvent[] => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  return events.filter(event => {
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);
    
    return eventStart < end && eventEnd >= start;
  });
};

export const createInvoice = async (invoiceData: any, token?: string): Promise<any> => {
  try {
    const response = await fetch(`${BASE_URL}/api/create_invoice`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Accept' : 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(invoiceData),
    });

    if (!response.ok) {
      throw new Error('Failed to create invoice');
    }

    const data = await response.json();
    clearEventsCache();
    return data.invoice;
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

export const convertEvents = (events: CalendarEvent[]) => {
  return events.map(event => convertToFullCalendarEvent(event));
};

export const getTeacherPrices = (): any[] => {
  return teacherPrices;
};

export const cacheEvents = (events: CalendarEvent[]): void => {
  return;
};
export const updateEventInCache = (eventId: string, updates: Partial<CalendarEvent['extendedProps']>): void => {
  return;
};

export const eventService = {
  getEvents,
  getStudents,
  getAgendaStudentsList,
  getTeachers,
  getCategories,
  getLocations,
  addStudent,
  addCategory,
  addLocation,
  createLesson,
  createSchoolLesson,
  convertEvents,
  convertToFullCalendarEvent,
  calculateDuration,
  calculateEndTime,
  calculatePrice,
  createInvoice,
  cacheEvents,
  getCachedEvents,
  clearEventsCache,
  getPendingBillings,
  getSchoolPendingBillings,
  getPendingValidationEvents,
  getSchoolPendingValidationEvents,
  updateEventInCache,
  filterEventsByDateRange,
  updateEvent,
  updateEventDateTime,
  deleteEvent,
  markEventAsCompleted,
  markEventsAsCompleted,
  deleteEvents,
  markEventAsUncompleted,
  updateStudentEventStatus
};
